/**
 * Utilities for managing session storage for event simulation cache
 */

export interface SimulationCacheData {
  searchData: {
    selectedCategories: string[];
    guests: string;
    budget: string;
    date: Date;
    cep: string;
    time?: string;
  };
  selectedSuppliers: { [key: string]: any[] };
  selectedDisposableProducts: any[];
  selectedDisposableItems: any[];
  selectedDecorationItems: any[];
  selectedSweetsItems: any[];
  selectedCakeItems: any[];
  currentViewingCategory: string;
  sessionId: string;
  timestamp: number;
  returnFromDetails?: boolean;
  simulationId?: string;
  itemImages?: {
    salgados: { [itemId: string]: string };
    doces: { [itemId: string]: string };
    decoracao: { [itemId: string]: string };
    descartaveis: { [itemId: string]: string };
  };
}

const SIMULATION_CACHE_KEY = 'event_simulation_cache';
const SESSION_ID_KEY = 'event_simulation_session_id';

/**
 * Generate a unique session ID for the current session
 */
export const generateSessionId = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get or create a session ID
 */
export const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
};

/**
 * Save simulation data to cache
 */
export const saveCacheData = (data: Partial<SimulationCacheData>): void => {
  try {
    // Proteção: não salva se não há dados ou se está em carregamento inicial
    if (!data || Object.keys(data).length === 0) {

      return;
    }
    
    const sessionId = getSessionId();
    const existingData = loadCacheData();
    
    // Verifica se realmente houve mudança nos dados
    const hasRealChanges = Object.keys(data).some(key => {
      const currentValue = existingData?.[key as keyof SimulationCacheData];
      const newValue = data[key as keyof SimulationCacheData];
      return JSON.stringify(currentValue) !== JSON.stringify(newValue);
    });
    
    if (!hasRealChanges) {

      return;
    }
    
    const cacheData: SimulationCacheData = {
      ...existingData,
      ...data,
      sessionId,
      timestamp: Date.now()
    };

    sessionStorage.setItem(SIMULATION_CACHE_KEY, JSON.stringify(cacheData));

  } catch (error) {
    console.error('Error saving cache data:', error);
  }
};

/**
 * Load simulation data from cache
 */
export const loadCacheData = (): SimulationCacheData | null => {
  try {
    const cachedData = sessionStorage.getItem(SIMULATION_CACHE_KEY);
    if (!cachedData) return null;

    const data = JSON.parse(cachedData);
    
    // Check if cache is still valid (24 hours)
    const isExpired = Date.now() - data.timestamp > 24 * 60 * 60 * 1000;
    if (isExpired) {
      clearCacheData();
      return null;
    }

    // Convert date string back to Date object if needed
    if (data.searchData && data.searchData.date && typeof data.searchData.date === 'string') {
      data.searchData.date = new Date(data.searchData.date);
    }

    return data;
  } catch (error) {
    console.error('Error loading cache data:', error);
    return null;
  }
};

/**
 * Clear simulation cache
 */
export const clearCacheData = (): void => {
  try {
    sessionStorage.removeItem(SIMULATION_CACHE_KEY);
  } catch (error) {
    console.error('Error clearing cache data:', error);
  }
};

/**
 * Check if there's valid cached data
 */
export const hasCachedData = (): boolean => {
  return loadCacheData() !== null;
};

/**
 * Update specific part of cached data
 */
export const updateCacheData = <K extends keyof SimulationCacheData>(
  key: K, 
  value: SimulationCacheData[K]
): void => {
  const existingData = loadCacheData() || {} as SimulationCacheData;
  saveCacheData({
    ...existingData,
    [key]: value
  });
};

/**
 * Save item image to cache by category
 */
export const cacheItemImage = (
  category: 'salgados' | 'doces' | 'decoracao' | 'descartaveis',
  itemId: string,
  imageUrl: string
): void => {
  try {
    const existingData = loadCacheData() || {} as SimulationCacheData;
    
    if (!existingData.itemImages) {
      existingData.itemImages = {
        salgados: {},
        doces: {},
        decoracao: {},
        descartaveis: {}
      };
    }

    existingData.itemImages[category][itemId] = imageUrl;
    saveCacheData(existingData);
    
  } catch (error) {
    console.error('Error caching item image:', error);
  }
};

/**
 * Get cached item image by category and ID
 */
export const getCachedItemImage = (
  category: 'salgados' | 'doces' | 'decoracao' | 'descartaveis',
  itemId: string
): string | null => {
  try {
    const cachedData = loadCacheData();
    if (!cachedData?.itemImages?.[category]) {
      return null;
    }
    
    return cachedData.itemImages[category][itemId] || null;
  } catch (error) {
    console.error('Error getting cached item image:', error);
    return null;
  }
};

/**
 * Clear all cached images
 */
export const clearImageCache = (): void => {
  try {
    const existingData = loadCacheData();
    if (existingData) {
      existingData.itemImages = {
        salgados: {},
        doces: {},
        decoracao: {},
        descartaveis: {}
      };
      saveCacheData(existingData);
    }
  } catch (error) {
    console.error('Error clearing image cache:', error);
  }
};

/**
 * Generate a unique cache key for an item
 */
export const generateItemCacheKey = (category: string, item: any): string => {
  
  switch (category) {
    case 'salgados':
      // For salgados/buffet items from suppliers
      const salgadosKey = `${item.supplier_id || item.id}_${item.product_id || item.id}`;
      return salgadosKey;
      
    case 'doces':
      // For gourmet sweets and cakes
      if (item.flavor || item.decoration) {
        // This is a cake
        const cakeKey = `cake_${item.decoration || 'default'}`;
        return cakeKey;
      } else {
        // This is gourmet candy
        const candyKey = `${item.product || item.name}_${item.sub_product || 'default'}`.replace(/\s+/g, '_');
        return candyKey;
      }
      
    case 'decoracao':
      const decoracaoKey = `${item.id || item.product_id}_${item.variant_id || 'default'}`;
      return decoracaoKey;
      
    case 'descartaveis':
      const descartaveisKey = `${item.id || item.product_id}_${item.variant_id || 'default'}`;
      return descartaveisKey;
      
    default:
      const defaultKey = `${item.id || 'unknown'}_${Date.now()}`;
      return defaultKey;
  }
};